import { Box, ButtonBase, Chip, Stack, Typography } from '@mui/material';
import type { GroupDinnerClaim } from '../../../types/groups';

/**
 * One person at one table, as one tap target.
 *
 * Deliberately not a row of icon buttons. Three 32px icons on a phone row — one
 * of which takes a seat away — is a misfire waiting to happen, and this screen
 * is used one-handed, standing in a restaurant, by somebody who is also talking
 * to a waiter. The whole row opens a sheet; every choice is made in there, where
 * the destructive one can be put last and behind its own step.
 *
 * The name shown is the ROSTER name whenever the claim is linked to a roster
 * row, which is what `claimDisplayName` resolves and what the seating chart
 * already renders. A claim row keeps its own copy of the name it was created
 * with, and a stale copy showing one person under two names on two screens is
 * how a place card and a guest list stop agreeing on the night.
 */
export default function ClaimRow({
  claim,
  displayName,
  busy,
  onOpen,
}: {
  claim: GroupDinnerClaim;
  /** Resolved by `claimDisplayName` — never the raw name sitting on the row. */
  displayName: string;
  /** A write on THIS claim is in flight. */
  busy: boolean;
  onOpen: () => void;
}) {
  /*
    A claim with no invitee id is a `+1` the roster never matched — the row type
    that no other endpoint in the API can reach, and the one most likely to need
    fixing at the door. The chip is also the honest explanation for why renaming
    works here and nowhere else on this list.
  */
  const isCompanion = !claim.inviteeId;

  return (
    <ButtonBase
      onClick={onOpen}
      disabled={busy}
      aria-label={`Edit ${displayName}’s seat`}
      aria-busy={busy}
      sx={{
        width: '100%',
        // 48px is the smallest target a thumb hits reliably; the claims list was
        // a 20px-tall Typography row before it was tappable.
        minHeight: 48,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        justifyContent: 'space-between',
        textAlign: 'left',
        opacity: busy ? 0.55 : 1,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap>{displayName}</Typography>
        {isCompanion ? <Chip size="small" variant="outlined" label="+1" /> : null}
      </Stack>
      <Box sx={{ flexShrink: 0, pl: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {claim.partySize > 1 ? `party of ${claim.partySize}` : '1 spot'}
        </Typography>
      </Box>
    </ButtonBase>
  );
}
